import React from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "@/contexts/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import AIChat from "@/components/AIChat";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "sonner";

// Wrapper: navbar only, no footer/chat, for the owner portal
function NavbarStandalone() { return <Navbar />; }

import Home from "@/pages/Home";
import About from "@/pages/About";
import HowItWorks from "@/pages/HowItWorks";
import Services from "@/pages/Services";
import Pricing from "@/pages/Pricing";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Careers from "@/pages/Careers";
import { Privacy, Terms } from "@/pages/Legal";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Booking from "@/pages/Booking";
import BookingResult from "@/pages/BookingResult";
import InviteClaim from "@/pages/InviteClaim";
import ParentDashboard from "@/pages/ParentDashboard";
import StudentDashboard from "@/pages/StudentDashboard";
import CleanerDashboard from "@/pages/CleanerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import OwnerLayout from "@/pages/owner/OwnerLayout";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import OwnerFinance from "@/pages/owner/OwnerFinance";
import OwnerCRM from "@/pages/owner/OwnerCRM";
import OwnerCommunities from "@/pages/owner/OwnerCommunities";
import OwnerContracts from "@/pages/owner/OwnerContracts";
import OwnerProposals from "@/pages/owner/OwnerProposals";
import OwnerPayroll from "@/pages/owner/OwnerPayroll";
import OwnerRoutes from "@/pages/owner/OwnerRoutes";

function PublicLayout() {
  const location = useLocation();
  const hideChromeOn = ["/login", "/register"];
  const hideChrome = hideChromeOn.includes(location.pathname);
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1"><Outlet /></main>
      {!hideChrome && <Footer />}
      <AIChat />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/services" element={<Services />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/booking" element={<Booking />} />
            <Route path="/booking/success" element={<BookingResult success />} />
            <Route path="/booking/cancel" element={<BookingResult success={false} />} />
            <Route path="/invite/:token" element={<InviteClaim />} />

            <Route path="/parent" element={<ProtectedRoute roles={["parent"]}><ParentDashboard /></ProtectedRoute>} />
            <Route path="/student" element={<ProtectedRoute roles={["student"]}><StudentDashboard /></ProtectedRoute>} />
            <Route path="/cleaner" element={<ProtectedRoute roles={["cleaner", "service_provider"]}><CleanerDashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute roles={["admin", "platform_admin", "university_admin", "community_manager"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/platform_admin" element={<ProtectedRoute roles={["platform_admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/university_admin" element={<ProtectedRoute roles={["university_admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/community_manager" element={<ProtectedRoute roles={["community_manager"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/service_provider" element={<ProtectedRoute roles={["service_provider"]}><CleanerDashboard /></ProtectedRoute>} />
          </Route>
          {/* Owner Operations Portal — full-height layout, no marketing chrome */}
          <Route path="/owner" element={<ProtectedRoute roles={["owner", "platform_admin", "admin"]}>
              <div className="min-h-screen flex flex-col">
                <NavbarStandalone />
                <OwnerLayout />
              </div>
            </ProtectedRoute>}>
            <Route index element={<OwnerDashboard />} />
            <Route path="finance" element={<OwnerFinance />} />
            <Route path="crm" element={<OwnerCRM />} />
            <Route path="communities" element={<OwnerCommunities />} />
            <Route path="contracts" element={<OwnerContracts />} />
            <Route path="proposals" element={<OwnerProposals />} />
            <Route path="payroll" element={<OwnerPayroll />} />
            <Route path="routes" element={<OwnerRoutes />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
